import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@/storybook/components/Dialog/Dialog';
import TabList, { Tab } from '@/storybook/components/TabList/TabList';
import Button from '@/storybook/components/Button/Button';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import { CustomColumnDef } from '@/types/table';
import { ColumnDisplayItem } from '@/types/table/columnConfig';
import { useTranslation } from 'react-i18next';
import { isStickyLeft } from '../../utils/columnLayout';
import {
  buildOrderItems,
  buildChildOrderMap,
  buildItemMap,
  computeHasOrderChanged,
  computeAllColumnIds,
  computeColumnIdsForOrder,
  ChildItem,
  orderItemsByIds,
  reconcileChildOrderMap,
  reconcileOrderIds,
} from './AdjustTableDialog.utils';
import ColumnReorder from './components/ColumnReorder';
import DisplayTab from './components/DisplayTab';
import PinningTab from './components/PinningTab';
import styles from './AdjustTableDialog.module.css';

type AdjustTableDialogTabId = 'order' | 'display' | 'pinning';

interface AdjustTableDialogColumnConfigProps {
  isLoading: boolean;
  displayItems: ColumnDisplayItem[];
  pinnedItems: ColumnDisplayItem[];
  unpinnedItems: ColumnDisplayItem[];
  hasChanges: boolean;
  onToggleVisibility: (columnId: string) => void;
  onTogglePinGroup: (columnIds: string[]) => void;
}

export type AdjustTableDialogProps<T> = {
  isOpen: boolean;
  onClose: VoidFunction;
  columns: CustomColumnDef<T>[];
  onApply: (columnOrder: string[], hasOrderChanged: boolean) => void | Promise<void>;
  onResetToDefault: VoidFunction | (() => Promise<void>);
  additionalHeaderLabels?: Record<string, string>;
  columnConfig?: AdjustTableDialogColumnConfigProps;
};

const createTrackedSetter =
  <T,>(
    setHasPendingOrderEdits: Dispatch<SetStateAction<boolean>>,
    setState: Dispatch<SetStateAction<T>>,
  ): Dispatch<SetStateAction<T>> =>
  nextState => {
    setHasPendingOrderEdits(true);
    setState(nextState);
  };

const buildOrderedColumnConfig = (
  columnConfig: AdjustTableDialogColumnConfigProps | undefined,
  pinnedOrder: string[],
  unpinnedOrder: string[],
  itemMap: Map<string, { id: string; label: string; columnIds: string[]; children: ChildItem[] }>,
  childOrderMap: Map<string, ChildItem[]>,
) => {
  if (!columnConfig) {
    return undefined;
  }

  const orderedPinnedIds = computeColumnIdsForOrder(pinnedOrder, itemMap, childOrderMap);
  const orderedUnpinnedIds = computeColumnIdsForOrder(unpinnedOrder, itemMap, childOrderMap);
  const orderedDisplayIds = [...orderedPinnedIds, ...orderedUnpinnedIds];

  return {
    ...columnConfig,
    displayItems: orderItemsByIds(columnConfig.displayItems, orderedDisplayIds),
    pinnedItems: orderItemsByIds(columnConfig.pinnedItems, orderedPinnedIds),
    unpinnedItems: orderItemsByIds(columnConfig.unpinnedItems, orderedUnpinnedIds),
  };
};

const buildFooter = (
  onResetToDefault: VoidFunction | (() => Promise<void>),
  onClose: VoidFunction,
  handleApply: VoidFunction,
  hasChanges: boolean,
  isLoading: boolean | undefined,
  resetLabel: ReactNode,
  cancelLabel: ReactNode,
  adjustLabel: ReactNode,
) => (
  <div className={styles.footer}>
    <Button variant="text" size="M" onClick={onResetToDefault}>
      {resetLabel}
    </Button>
    <div className={styles.footerRight}>
      <Button variant="outlined" size="M" onClick={onClose}>
        {cancelLabel}
      </Button>
      <Button variant="primary" size="M" disabled={!hasChanges || isLoading} onClick={handleApply}>
        {adjustLabel}
      </Button>
    </div>
  </div>
);

const AdjustTableDialog = <T,>({
  isOpen,
  onClose,
  columns,
  onApply,
  onResetToDefault,
  additionalHeaderLabels,
  columnConfig,
}: AdjustTableDialogProps<T>) => {
  const { t } = useTranslation();

  const tabs = useMemo<Tab<AdjustTableDialogTabId>[]>(() => {
    const orderTab: Tab<AdjustTableDialogTabId> = { id: 'order', label: t('global.table.order') };
    if (!columnConfig) {
      return [orderTab];
    }
    return [
      { id: 'display', label: t('global.table.columnConfig.removeAddTab') },
      orderTab,
      { id: 'pinning', label: t('global.table.columnConfig.pinningTab') },
    ];
  }, [columnConfig, t]);

  const visibleColumns = useMemo(
    () => columns.filter(column => column.visible === undefined || column.visible),
    [columns],
  );

  const initialPinnedItems = useMemo(
    () =>
      buildOrderItems(
        visibleColumns.filter(column => isStickyLeft(column.sticky)),
        additionalHeaderLabels,
      ),
    [visibleColumns, additionalHeaderLabels],
  );

  const initialUnpinnedItems = useMemo(
    () =>
      buildOrderItems(
        visibleColumns.filter(column => !column.sticky),
        additionalHeaderLabels,
      ),
    [visibleColumns, additionalHeaderLabels],
  );

  const initialPinnedIds = useMemo(() => initialPinnedItems.map(pinnedItem => pinnedItem.id), [initialPinnedItems]);

  const initialUnpinnedIds = useMemo(
    () => initialUnpinnedItems.map(unpinnedItem => unpinnedItem.id),
    [initialUnpinnedItems],
  );

  const allInitialItems = useMemo(
    () => [...initialPinnedItems, ...initialUnpinnedItems],
    [initialPinnedItems, initialUnpinnedItems],
  );
  const initialChildOrderMap = useMemo(() => buildChildOrderMap(allInitialItems), [allInitialItems]);

  const [pinnedOrder, setPinnedOrder] = useState<string[]>(() => initialPinnedIds);
  const [unpinnedOrder, setUnpinnedOrder] = useState<string[]>(() => initialUnpinnedIds);
  const [childOrderMap, setChildOrderMap] = useState<Map<string, ChildItem[]>>(() => initialChildOrderMap);
  const [hasPendingOrderEdits, setHasPendingOrderEdits] = useState(false);
  const [currentTab, setCurrentTab] = useState<Tab<AdjustTableDialogTabId>>(() => tabs[0]);
  const previousIsOpenRef = useRef(isOpen);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      setPinnedOrder(initialPinnedIds);
      setUnpinnedOrder(initialUnpinnedIds);
      setChildOrderMap(initialChildOrderMap);
      setHasPendingOrderEdits(false);
      setCurrentTab(tabs[0]);
    }
  }, [initialChildOrderMap, initialPinnedIds, initialUnpinnedIds, isOpen, tabs]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!hasPendingOrderEdits) {
      setPinnedOrder(initialPinnedIds);
      setUnpinnedOrder(initialUnpinnedIds);
      setChildOrderMap(initialChildOrderMap);
      return;
    }

    setPinnedOrder(currentPinnedOrder => reconcileOrderIds(currentPinnedOrder, initialPinnedIds));
    setUnpinnedOrder(currentUnpinnedOrder => reconcileOrderIds(currentUnpinnedOrder, initialUnpinnedIds));
    setChildOrderMap(currentChildOrderMap => reconcileChildOrderMap(currentChildOrderMap, initialChildOrderMap));
  }, [hasPendingOrderEdits, initialChildOrderMap, initialPinnedIds, initialUnpinnedIds, isOpen]);

  useEffect(() => {
    if (!tabs.some(tab => tab.id === currentTab.id)) {
      setCurrentTab(tabs[0]);
    }
  }, [currentTab.id, tabs]);

  const itemMap = useMemo(() => buildItemMap(allInitialItems), [allInitialItems]);
  const handlePinnedOrderChange = createTrackedSetter(setHasPendingOrderEdits, setPinnedOrder);
  const handleUnpinnedOrderChange = createTrackedSetter(setHasPendingOrderEdits, setUnpinnedOrder);
  const handleChildOrderMapChange = createTrackedSetter(setHasPendingOrderEdits, setChildOrderMap);

  const hasOrderChanged = useMemo(
    () =>
      computeHasOrderChanged(pinnedOrder, unpinnedOrder, initialPinnedIds, initialUnpinnedIds, childOrderMap, itemMap),
    [pinnedOrder, unpinnedOrder, initialPinnedIds, initialUnpinnedIds, childOrderMap, itemMap],
  );
  const orderedColumnConfig = useMemo(
    () => buildOrderedColumnConfig(columnConfig, pinnedOrder, unpinnedOrder, itemMap, childOrderMap),
    [childOrderMap, columnConfig, itemMap, pinnedOrder, unpinnedOrder],
  );
  const hasChanges = hasOrderChanged || Boolean(columnConfig?.hasChanges);

  const handleApply = async () => {
    const allColumnIds = computeAllColumnIds(pinnedOrder, unpinnedOrder, itemMap, childOrderMap);
    await onApply(allColumnIds, hasOrderChanged);
  };

  const footer = buildFooter(
    onResetToDefault,
    onClose,
    handleApply,
    hasChanges,
    columnConfig?.isLoading,
    t('global.table.resetToDefault'),
    t('global.table.cancel'),
    t('global.table.adjust'),
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('global.table.manageColumns')}
      size="M"
      footer={footer}
      data-testid="adjust-table-dialog"
      separatorClassName={styles.hiddenSeparator}
      fixedContentClassName={styles.fixedContentNoPaddingBottom}
      scrollableContentClassName={styles.scrollableContentNoPadding}
      fixedContent={
        <div className={styles.tabsWrapper}>
          <TabList tabs={tabs} currentTab={currentTab} setCurrentTab={setCurrentTab} />
        </div>
      }
    >
      {currentTab.id === 'order' && (
        <ColumnReorder
          pinnedOrder={pinnedOrder}
          unpinnedOrder={unpinnedOrder}
          itemMap={itemMap}
          childOrderMap={childOrderMap}
          setPinnedOrder={handlePinnedOrderChange}
          setUnpinnedOrder={handleUnpinnedOrderChange}
          setChildOrderMap={handleChildOrderMapChange}
        />
      )}
      {currentTab.id !== 'order' && orderedColumnConfig?.isLoading && (
        <div className={styles.loadingState}>
          <LoadingSpinner testId="column-config-loading" />
        </div>
      )}
      {currentTab.id !== 'order' && orderedColumnConfig && !orderedColumnConfig.isLoading && (
        <>
          {currentTab.id === 'display' && (
            <DisplayTab
              items={orderedColumnConfig.displayItems}
              pinnedItems={orderedColumnConfig.pinnedItems}
              onToggleVisibility={orderedColumnConfig.onToggleVisibility}
            />
          )}
          {currentTab.id === 'pinning' && (
            <PinningTab
              pinnedItems={orderedColumnConfig.pinnedItems}
              unpinnedItems={orderedColumnConfig.unpinnedItems}
              onTogglePinGroup={orderedColumnConfig.onTogglePinGroup}
            />
          )}
        </>
      )}
    </Dialog>
  );
};

export default AdjustTableDialog;
